import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { MOCK_WORKFLOW_DEFINITION } from '~/dev/utils/MockService/handlers/k2/mockWorkflowDefinition';
import { Button } from '~/eds/src/components/Button/Button';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { Table } from '~/eds/src/modules/Table/Table';
import type { Workflow } from '~/features/workflows-clm-evolution/types';
import { api } from '~/redux/api/createApi';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';

const messages = defineMessages({
  pageTitle: {
    id: 'workflowCLMEvolutionWorkflows.pageTitle',
    defaultMessage: 'Workflows',
    description: 'Title of the workflows page',
  },
  createWorkflow: {
    id: 'workflowCLMEvolutionWorkflows.createWorkflow',
    defaultMessage: 'Create Workflow',
    description: 'Button text to create a new workflow',
  },
  name: {
    id: 'workflowCLMEvolutionWorkflows.name',
    defaultMessage: 'Name',
    description: 'Name of the workflow',
  },
  version: {
    id: 'workflowCLMEvolutionWorkflows.version',
    defaultMessage: 'Version',
    description: 'Version of the workflow',
  },
  createdAt: {
    id: 'workflowCLMEvolutionWorkflows.createdAt',
    defaultMessage: 'Created',
    description: 'Created at date of the workflow',
  },
});

const WorkflowCLMEvolutionWorkflowsPage = () => {
  const intl = useIntl();
  const { navigate } = useRouting();
  const { data, isLoading } = api.endpoints.listWorkflowsK2.useQuery({});
  const [createWorkflow, { isLoading: isCreating }] =
    api.endpoints.createWorkflowK2.useMutation();

  const handleCreateWorkflow = () => {
    const uniqueId = Math.floor(Math.random() * 10000);
    createWorkflow({
      workflowDefinition: {
        ...MOCK_WORKFLOW_DEFINITION,
        name: `Workflow ${uniqueId}`,
      },
    });
  };

  const columns = useMemo(
    () => [
      {
        key: 'name',
        title: intl.formatMessage(messages.name),
        cellType: 'text',
        mapCellProps: (workflow: Workflow) => ({
          text: workflow.workflow_definition?.name ?? '',
        }),
      },
      {
        key: 'version',
        title: intl.formatMessage(messages.version),
        cellType: 'number',
        mapCellProps: (workflow: Workflow) => ({
          number: workflow.version,
        }),
      },
      {
        key: 'createdAt',
        title: intl.formatMessage(messages.createdAt),
        cellType: 'datetime',
        mapCellProps: (workflow: Workflow) => ({
          datetime: workflow.created_at,
          format: 'short_datetime',
        }),
      },
    ],
    [intl],
  );

  return (
    <PageLayout
      title={intl.formatMessage(messages.pageTitle)}
      headerCalloutContent={
        <Button
          text={intl.formatMessage(messages.createWorkflow)}
          variant="primary"
          isLoading={isCreating}
          onClick={handleCreateWorkflow}
        />
      }
    >
      <ContentContainer loadingContent={{ isLoading }}>
        <Table
          name="workflows"
          columns={columns}
          data={data?.data ?? []}
          isLoading={isLoading}
          rowDetails={{
            onClick: (workflow: Workflow) => {
              navigate(
                RoutePathType.WorkflowCLMEvolutionWorkflowsDetail.replace(
                  ':workflowId',
                  workflow.id,
                ),
              );
            },
          }}
        />
      </ContentContainer>
    </PageLayout>
  );
};

export default WorkflowCLMEvolutionWorkflowsPage;
